/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import firebase from 'firebase/compat/app';
import {
	FbdbRequestConfig,
	ICsvPlanConfig,
	ICsvProjectConfig,
	IExportTicketsConfig,
	IFbdbRequestPlanProjectResults,
} from './fbdb-requests.interfaces';
import { FbdbRequests } from './fbdb-requests.service';
import { catchError, switchMap, take } from 'rxjs/operators';
import { combineLatest, from, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment-variables';
import { UploadTaskSnapshot } from '@angular/fire/compat/storage/interfaces';
import { AppState } from './app-state.service';
import { ExportFormatType } from '../modules/reports/types/ExportFormatType';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';

export type FirebaseStorageMetadata = firebase.storage.FullMetadata;

@Injectable({
	providedIn: 'root',
})
export class CloudStorageService {
	public storage = firebase.storage();
	public storageRef: firebase.storage.Reference;
	private logger: ILoggerService = LoggerServiceFactory.create('CloudStorageService');

	constructor(private fbdbRequestsService: FbdbRequests, private appState: AppState) {}

	public async getCloudURL(path: string, bucket?: string): Promise<string> {
		if (!path) {
			return Promise.resolve('');
		}
		// use try-catch to prevent firebase throwing errors
		let url = null;
		try {
			if (bucket) {
				const storageWithSpecifiedBucket = firebase.app().storage(bucket);
				this.storageRef = storageWithSpecifiedBucket.ref(path);

				url = await this.storageRef.getDownloadURL();
				return url;
			}

			this.storageRef = this.storage.ref(path);

			url = await this.storageRef.getDownloadURL();
		} catch (error) {
			// return null if path doesn't exist
			this.logger.warn(error);
			return null;
		}
		return url;
	}

	/**
	 * Upload file to GCS bucket with provided metadata and given path, promise version
	 * @param file file
	 * @param metadata file metadata
	 * @param path path on bucket
	 * @param bucket bucket
	 * @returns promise of UploadTaskSnapshot
	 */
	public async uploadFile(
		file: File | Blob,
		metadata: firebase.storage.UploadMetadata,
		fileId: string,
		path: string,
		bucket: string = environment.firebase.storageBucket
	): Promise<UploadTaskSnapshot> {
		const bucketRef = firebase.app().storage(bucket).ref();
		//TODO: ...this await does nothing?
		const storageRef = await bucketRef.child(environment.refUrl).child(path).child(fileId);
		const promise = storageRef.put(file, metadata as firebase.storage.UploadMetadata);
		return promise;
	}

	/**
	 * Upload file to GCS bucket with provided metadata and given path, observable version.
	 * Will complete when the upload is done. Also currently emits once, don't rely on that though.
	 * @param file file
	 * @param metadata file metadata
	 * @param fileId file id
	 * @param path path on bucket
	 * @param bucket bucket
	 * @returns Observable of UploadTaskSnapshot
	 */
	public uploadFile$(
		file: File | Blob,
		metadata: firebase.storage.UploadMetadata,
		fileId: string,
		path: string,
		bucket: string = environment.firebase.storageBucket
	): Observable<UploadTaskSnapshot> {
		return from(this.uploadFile(file, metadata, fileId, path, bucket));
	}

	/**
	 * Returns an Observable that yields a pair of:
	 *    - request results (including intermediate updates)
	 *    - the URL to access the generated CSV data
	 * Only the last pairs will have a valid CSV URL; before that, the URLs will be ''
	 */
	public requestPlanCsvURL(
		projectId: string,
		planId: string,
		fbfbConfig?: FbdbRequestConfig
	): Observable<[IFbdbRequestPlanProjectResults, string]> {
		const config: ICsvPlanConfig = { projectId, planId };
		return this.fbdbRequestsService
			.requestPlanTable(config, fbfbConfig)
			.pipe(switchMap((results) => this.augmentCsvRequestResultsWithCloudUrl$(results)));
	}

	/**
	 * Returns an Observable that yields a pair of:
	 *    - request results (including intermediate updates)
	 *    - the URL to access the generated CSV data
	 * Only the last pairs will have a valid CSV URL; before that, the URLs will be ''
	 */
	public requestProjectCsvURL(
		projectId: string,
		includeArchivedPlans?: boolean,
		fbfbConfig?: FbdbRequestConfig
	): Observable<[IFbdbRequestPlanProjectResults, string]> {
		const config: ICsvProjectConfig = { projectId, includeArchivedPlans };
		return this.fbdbRequestsService
			.requestProjectTable(config, fbfbConfig)
			.pipe(switchMap((results) => this.augmentCsvRequestResultsWithCloudUrl$(results)));
	}

	public requestTicketExport(config: IExportTicketsConfig) {
		const fbdbRequestId = this.fbdbRequestsService.getNewRequestId();
		if (config.exportTicketsFormat === ExportFormatType.P6Excel) {
			const boilerplate = this.collectP6Boilerplate(config.projectId);
			config.gcsBucket = boilerplate.bucket;

			config.gcsBlob = sanitizeGcsPath(boilerplate.path + '/' + boilerplate.pathname);
		}
		return this.fbdbRequestsService
			.requestExportTickets(config, fbdbRequestId)
			.pipe(switchMap((results) => this.augmentCsvRequestResultsWithCloudUrl$(results)));
	}

	public uploadP6Template(projectId: string, file: File) {
		const boilerplate = this.collectP6Boilerplate(projectId);

		const fileName = file.name || boilerplate.pathname;

		return this.uploadFile$(
			file,
			{
				customMetadata: { clientFileName: fileName },
				contentDisposition: `fileName="${fileName}"`,
			},
			boilerplate.pathname,
			boilerplate.pathWithoutRefUrl,
			boilerplate.bucket
		);
	}

	public checkForExistingP6Template(projectId: string): Promise<FirebaseStorageMetadata> {
		const boilerplate = this.collectP6Boilerplate(projectId);

		const bucketRef = firebase.app().storage(boilerplate.bucket).ref();
		const storageRef = bucketRef.child(boilerplate.path).child(boilerplate.pathname);
		return storageRef.getMetadata();
	}

	/**
	 * Checks if a file exists on cloud storage. Will emit once then complete
	 * @returns an observable that contains file metadata if the file exists, or null if it does not.
	 */
	public checkForExistingP6Template$(projectId: string): Observable<FirebaseStorageMetadata> {
		return from(this.checkForExistingP6Template(projectId)).pipe(
			catchError((e) => {
				if (e?.code === 'storage/object-not-found') {
					this.logger.warn('expected storage/object-not-found', e);
					return of(null);
				}
				this.logger.error(e);
				throw e;
			}),
			take(1)
		);
	}

	private collectP6Boilerplate(projectId: string) {
		const userId = this.appState.userSync.$id;
		return {
			bucket: environment.firebase.attachmentBucket,
			userId,
			pathname: 'p6template.xlsx',
			path: sanitizeGcsPath(
				`${environment.refUrl}projectFiles/projectUserAttachments/${projectId}/${userId}`
			),
			pathWithoutRefUrl: sanitizeGcsPath(`projectFiles/projectUserAttachments/${projectId}/${userId}`),
		};
	}

	private augmentCsvRequestResultsWithCloudUrl$(
		results: IFbdbRequestPlanProjectResults
	): Observable<[IFbdbRequestPlanProjectResults, string]> {
		const blob = results.csvBlob ?? results.gcsBlob;
		const bucket = results.csvBucket ?? results.gcsBucket;
		return combineLatest([of(results), from(this.getCloudURL(blob, bucket))]);
	}
}

//rips out any double slashes resulting from weird config values
function sanitizeGcsPath(path: string) {
	return path
		.split('/')
		.filter((s) => s.length > 0)
		.join('/');
}
